import Guid from "_shared/guids/guid";
import _ from "lodash";
import Forum from "messaging/forums/forum";
import Topic from "messaging/forums/topics/topic";
import AHDate from "_shared/dates/date";
import Proposal from "work/proposals/proposal";
import { Masked } from "work/masked-fields";
import EntityMember from "_shared/entity-members/entity-member";
import CommentThreadSchema, {
  CommentThreadJSON,
  CommentThreadParams,
} from "work/proposals/comments/threads/_schemas/comment-thread-schema";
import { Audience, TopicContext } from "work/proposals/comments/threads/topics/topic-context";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumName from "notifications/message-notifications/forum-info/forum-name";

export { Audience };

/**
 * A proposal's comment thread: one per audience, for the whole negotiation.
 *
 * Threads used to be keyed by proposal field (and, for redlines, by collection item) as well as
 * audience, so a negotiation accumulated a forum per field per audience and the same conversation was
 * split across a dozen places the reader had to go looking for. A proposal now has exactly two threads
 * a given actor can write to — their own party's, and the one both parties can read — and the pane
 * shows them as a single stream interleaved with the change history.
 *
 * Threads that predate the consolidation still load (their comments are history and belong in the
 * stream); they are simply never written to again, because a comment is always posted through
 * {@link CommentThread.forProposal}, which names the audience and nothing else. The server collapses
 * any remaining per-field context onto the same audience forum — see `ProposalCommentForumTopic`.
 */
export default class CommentThread {
  public static readonly EXAMPLE = new CommentThread({
    proposalId: Guid.generate(),
    subscribers: [],
    isExternal: false,
    id: Guid.generate(),
    createdDate: new AHDate(),
    numUnreadMessages: 0,
    isResolved: false,
  });

  public static fromJSON(json: CommentThreadJSON | null | undefined): CommentThread | null | undefined {
    if (!json) return json;
    const parsed = CommentThreadSchema.deserialize(json);
    return new CommentThread(parsed);
  }

  /**
   * The thread a comment the actor is writing belongs to. Deliberately carries no forum id: the server
   * get-or-creates by (negotiation, audience), so every actor's post lands in the one thread that
   * audience owns without the browser having to have loaded it first.
   */
  public static forProposal(params: {
    proposal: Proposal;
    isViewingAsVendor: boolean;
    isExternal?: boolean;
  }): CommentThread {
    const { proposal, isViewingAsVendor } = params;
    const isExternal = params.isExternal ?? false;
    if (!proposal.id) {
      throw new InvalidCommentThreadError("Proposal id is missing");
    }
    const subscribers = getSubscribersForProposal({ isExternal, proposal, isViewingAsVendor });
    return new CommentThread({ proposalId: proposal.id, subscribers, isExternal });
  }

  public static fromForum(forum: Forum): CommentThread {
    if (forum.topic?.entityClass?.value !== "Work.Proposal") {
      throw new InvalidCommentThreadError("Forum is not a proposal forum");
    }
    if (!forum.topic?.entityId) {
      throw new InvalidCommentThreadError("Proposal id is missing on comment forum");
    }
    if (!forum.topic?.context) {
      throw new InvalidCommentThreadError("Proposal context is missing on comment forum");
    }

    return new CommentThread({
      proposalId: forum.topic.entityId,
      subscribers: forum.subscribers ?? [],
      isExternal: TopicContext.audienceIsExternal(TopicContext.audienceFromContext(forum.topic.context)),
      id: forum.id,
      createdDate: forum.createdDate,
      numUnreadMessages: forum.numUnreadMessages ?? 0,
    });
  }

  public id?: Guid;
  public proposalId: Guid;
  public isExternal: boolean;
  public subscribers: EntityMember[];
  public createdDate?: AHDate;
  public isResolved?: boolean;
  public numUnreadMessages?: number;

  constructor(params: {
    proposalId: Guid;
    subscribers?: EntityMember[];
    isExternal?: boolean;
    id?: Guid;
    createdDate?: AHDate;
    numUnreadMessages?: number;
    isResolved?: boolean;
  }) {
    this.id = params.id;
    this.proposalId = params.proposalId;
    this.subscribers = params.subscribers ?? [];
    this.isExternal = params.isExternal ?? false;
    this.createdDate = params.createdDate;
    this.numUnreadMessages = params.numUnreadMessages ?? 0;
    this.isResolved = params.isResolved ?? false;
  }

  /**
   * A copy of this thread with `patch` applied, mirroring `Comment.with`.
   *
   * One `CommentThread` instance is routinely held by several comments in the same conversation, so
   * changing a thread by writing into it changes rows nobody touched — and React, which is handed the
   * same object it had before, never learns that anything changed at all. Anything that alters a
   * thread therefore produces a new one; see `reaimCommentAtAudience`, the only such change a reader
   * can make.
   */
  public with(patch: Partial<CommentThreadParams>): CommentThread {
    return new CommentThread({ ...(this as CommentThreadParams), ...patch });
  }

  public getAudience(isViewingAsVendor: boolean): Audience {
    if (this.isExternal) {
      return Audience.AllReviewers;
    }
    return isViewingAsVendor ? Audience.VendorReviewers : Audience.ClientReviewers;
  }

  public toForum(params: { isViewingAsVendor: boolean }): Forum {
    const { isViewingAsVendor } = params;
    const audience = this.getAudience(isViewingAsVendor);
    return Forum.create({
      name: new ForumName(TopicContext.GeneralField),
      topic: Topic.create({
        entityClass: new ForumEntityClass("Work.Proposal"),
        entityId: this.proposalId,
        context: JSON.stringify(new TopicContext(audience).toJSON()),
      }),
      subscribers: this.subscribers,
      id: this.id,
      creator: undefined,
      createdDate: this.createdDate,
      ...params,
    });
  }

  public toJSON(): CommentThreadJSON {
    return CommentThreadSchema.serialize(this);
  }

  public isEqualTo(other: CommentThread): boolean {
    if (!other) return false;
    if (this.id && other.id) {
      return this.id.isEqualTo(other.id);
    }
    return this.proposalId.isEqualTo(other.proposalId) && this.isExternal === other.isExternal;
  }
}

export class InvalidCommentThreadError extends Error {
  constructor(message: string) {
    super(message || `Invalid comment thread`);
  }
}

const getSubscribersForProposal = (params: {
  isExternal: boolean;
  proposal: Proposal;
  isViewingAsVendor: boolean;
}): EntityMember[] => {
  const { isExternal, proposal, isViewingAsVendor } = params;
  let subscribers: EntityMember[];
  if (isExternal) {
    subscribers = getAllSubscribers(proposal);
  } else if (isViewingAsVendor) {
    subscribers = getVendorSubscribers(proposal);
  } else {
    subscribers = getClientSubscribers(proposal);
  }
  subscribers = _.uniqBy(subscribers, (sub) => sub.userId.value);
  return subscribers;
};

const getClientSubscribers = (proposal: Proposal): EntityMember[] => {
  const clientSubscribers: EntityMember[] = [];
  if (Masked.isNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId) {
    clientSubscribers.push(proposal.clientLeader);
  }

  clientSubscribers.push(...proposal.clientReviewers.map((reviewer) => reviewer.entityMember));
  return clientSubscribers;
};

const getVendorSubscribers = (proposal: Proposal): EntityMember[] => {
  const vendorSubscribers: EntityMember[] = [];

  if (Masked.isMasked(proposal.vendorTeam)) {
    return vendorSubscribers;
  }
  if (!proposal.vendorTeam) {
    return vendorSubscribers;
  }

  if (proposal.vendorTeam?.leader?.userId && proposal.vendorTeam.leader.legalEntityId) {
    vendorSubscribers.push(proposal.vendorTeam.leader);
  }
  vendorSubscribers.push(...proposal.vendorReviewers.map((reviewer) => reviewer.entityMember));
  return vendorSubscribers;
};

const getAllSubscribers = (proposal: Proposal): EntityMember[] => {
  const allSubscribers: EntityMember[] = [];
  if (Masked.isNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId) {
    allSubscribers.push(proposal.clientLeader, ...proposal.clientReviewers.map((reviewer) => reviewer.entityMember));
  }

  if (Masked.isMasked(proposal.vendorTeam)) return allSubscribers;

  if (proposal.vendorTeam?.leader)
    allSubscribers.push(
      proposal.vendorTeam.leader,
      ...proposal.vendorReviewers.map((reviewer) => reviewer.entityMember)
    );

  return allSubscribers;
};
